import { FC, PropsWithChildren, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { cn } from "@/lib/cn";
import { createOperationClient } from "@/services/react-query.service/graphql-client";

import { createDownloadLinkMutationDocument } from "../hooks/api/signed-url.gql";

interface DownloadLink {
  id: string;
}

export interface FileDownloaderProps extends PropsWithChildren {
  className?: string;
  file: {
    id: string;
    name: string;
  };
  mode: "DOWNLOAD" | "OPEN";
}

export const FileDownloader: FC<FileDownloaderProps> = ({ className, children, file, mode = "OPEN" }) => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation("common");

  function showErrorToast() {
    toast.error(t("fileDownload.errorTitle"), { description: t("fileDownload.errorDescription") });
  }

  async function getDownloadLink(): Promise<DownloadLink | null> {
    const client = createOperationClient("createDownloadLink");
    const data = await client.request(createDownloadLinkMutationDocument, {
      input: {
        fileId: file.id,
      },
    });

    if (!data) {
      return null;
    }

    return data.createDownloadLink;
  }

  function triggerDownload(downloadLinkId: string) {
    const aTag = document.createElement("a");
    const apiEndpoint = import.meta.env.VITE_API_ENDPOINT;
    aTag.href = `${apiEndpoint}/download/${downloadLinkId}${mode === "DOWNLOAD" ? "?download=1" : ""}`;
    aTag.setAttribute("target", "_blank");
    aTag.setAttribute("download", file.name);
    document.body.append(aTag);
    aTag.click();
    aTag.remove();
  }

  async function downloadFile() {
    if (loading) {
      return;
    }

    setLoading(true);
    const downloadLink = await getDownloadLink();

    if (!downloadLink) {
      showErrorToast();
      setLoading(false);
      return;
    }

    triggerDownload(downloadLink.id);
    setLoading(false);
  }

  function handleKeyDown(event: React.KeyboardEvent) {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    downloadFile();
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className={cn([
        "cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus",
        className,
      ])}
      onClick={() => {
        downloadFile();
      }}
      onKeyDown={handleKeyDown}
    >
      {children}
    </div>
  );
};
